<script setup lang="ts">
import { WidthVariants } from '~/types/Content'
import { cardTransformer, CardFields } from './cardHelpers'

const MAX_CARDS = 5

type ProofPointProps = {
  cards: CardFields[]
  references?: [
    {
      fields: {
        title?: string
      }
    },
  ]
  width?: WidthVariants
  supportingData?: {
    home?: {
      variant?: {
        children?: {
          mobileColumns?: {
            value?: string
          }
        }
      }
    }
  }
}
const props = defineProps<ProofPointProps>()
const reference = props?.references?.[0]?.fields
const title = reference?.title

const visibleCards = computed(() => props.cards?.slice(0, MAX_CARDS) || [])
const mobileColumns = props.supportingData?.home?.variant?.children?.mobileColumns?.value === '2' ? 2 : 1
const cardsClasses = computed(() => [
  `proof-point-panel__cards--mobile-columns-${mobileColumns}`,
  `proof-point-panel__cards--count-${visibleCards.value.length}`,
])
</script>

<template>
  <section class="proof-point-panel" data-testid="proof-point-panel">
    <div class="container" :class="{ 'container-full': width === 'wide' }">
      <div class="proof-point-panel__header">
        <h3 class="proof-point-panel__title" v-if="title">{{ title }}</h3>
      </div>
      <div class="proof-point-panel__cards" :class="cardsClasses" data-testid="proof-point-panel__cards">
        <CardLink
          class="proof-point-panel__card"
          data-testid="proof-point-panel__card"
          v-for="card in visibleCards"
          v-bind="cardTransformer(card)"
          preset="icon"
        />
      </div>
    </div>
  </section>
</template>

<style lang="postcss">
.proof-point-panel {
  margin-bottom: var(--component-space);
  margin-inline: var(--space-50);

  .container {
    border-top: 1px solid var(--ui-neutral-20);
    border-bottom: 1px solid var(--ui-neutral-20);

    /* The cards row owns the inline inset from tablet up, so it isn't added to the container's */
    @media (--tablet) {
      padding-inline: 0;
    }
  }

  .proof-point-panel__cards {
    --proof-point-columns: none;
    --proof-point-gap: var(--space-30);

    display: grid;
    gap: var(--space-30) var(--space-60);

    @media (--tablet) {
      display: flex;
      justify-content: space-evenly;
      gap: 0; /* Cards are separated by their own inline padding, not a row gap */
    }
  }

  .proof-point-panel__cards--count-3,
  .proof-point-panel__cards--count-4,
  .proof-point-panel__cards--count-5 {
    @media (--tablet) {
      padding-inline: var(--space-100);
    }
  }

  /* A wordmark only fits beside the text once the card is wide enough; 4-5 cards wait for desktop */
  .proof-point-panel__cards--count-1,
  .proof-point-panel__cards--count-2,
  .proof-point-panel__cards--count-3 {
    @media (--tablet) {
      --proof-point-columns: auto minmax(0, 1fr);
      --proof-point-gap: var(--space-50);
    }
  }

  .proof-point-panel__cards--count-4,
  .proof-point-panel__cards--count-5 {
    @media (--desktop) {
      --proof-point-columns: auto minmax(0, 1fr);
      --proof-point-gap: var(--space-50);
    }
  }

  .proof-point-panel__cards--mobile-columns-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .proof-point-panel__card {
    @media (--tablet) {
      display: grid; /* Lets the link wrapper fill the equal-height card so its content can centre */
      flex: 1 1 0;
      min-width: 0;
      max-width: var(--text-max-width);
    }

    a,
    .card__wrap-no-link {
      padding: var(--space-60) 0;
      display: grid;
      align-content: start;
      grid-template-columns: var(--proof-point-columns);
      gap: var(--proof-point-gap);

      @media (--tablet) {
        padding-inline: var(--space-50);
      }
    }

    /* Card.vue crops .card__media to a 4/5 product ratio; proof point logos keep their intrinsic size */
    .card__media {
      align-self: start;
      aspect-ratio: auto;
      overflow: visible;

      /* Block so the logo sits flush; inline boxes reserve descender space beneath it */
      > * {
        display: block;
        height: auto;
        width: auto;
      }
    }

    /* For viewBox-only SVGs without intrinsic size, `height: auto` leaves the auto-width media column nothing to size from
       and it collapses to 0. A definite height --icon-height (from Icon.vue) keeps assets under the cap at their own size. */
    .icon__img {
      display: block;
      height: min(var(--icon-height, 1.25rem), 1.25rem);
      width: auto;
      max-width: 100%;
    }

    p {
      margin: 0;
    }
  }
}
</style>
.
